import ApiService from '../../components/api_services/apiService.jsx';
import ActionMenu from '../../components/components/action_menu/ActionMenu.jsx';

import { ReactComponent as StatusUp } from '../../components/icons/status-up.svg';
import { ReactComponent as Edit } from '../../components/icons/edit.svg';
import { ReactComponent as TaskSquare } from '../../components/icons/task-square.svg';
import { ReactComponent as Pause } from '../../components/icons/pause.svg';
import { ReactComponent as Play } from '../../components/icons/play.svg';
import { ReactComponent as TickCircle } from '../../components/icons/tick-circle.svg';
import { ReactComponent as Trash } from '../../components/icons/trash.svg';
import { ReactComponent as Danger } from '../../components/icons/danger.svg';

import {
  toggleCheckedItem,
  openEditModal,
  toggleAddModal,
  setEditedEvent,
  setAddEventMode,
  setDuplicatedEventId,
  setModalViewActualTab,
  toggleNeedRefresh,
  setPendingEditReview,
  clearPendingEditReview,
  setTotalCount,
} from '../../store/eventsSlice';
import { setSelectedEvent } from '../../store/statsSlice.js';
import {
  isEventActive,
  isEventEnded,
  isPendingDeactivation,
  isPendingActivation,
} from '../../utils/eventStatus.js';

const formatDate = (utcString) => {
  if (!utcString) {
    return '-';
  }

  const date = new Date(utcString);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isApprovedByAdmin = (event) => {
  if (typeof event?.approvedByAdmin === 'boolean') {
    return event.approvedByAdmin;
  }
  return event?.isApprovedByAdmin;
};

const isEventMarkedActive = (event) => {
  if (typeof event?.active === 'boolean') {
    return event.active;
  }
  return event?.isActive;
};

export const getEventStatusLabel = (event, matchingPendingItem = null) => {
  if (event?.hasPendingEdit === true) {
    const pendingItem = matchingPendingItem || event?.pendingItem || event?.pendingChange || null;
    if (isPendingDeactivation(pendingItem, event)) {
      return 'Dezaktywacja';
    }
    if (isPendingActivation(pendingItem, event)) {
      return 'Aktywacja';
    }
    return 'Zmiana danych';
  }

  if (isApprovedByAdmin(event) === false) {
    return 'Oczekujace';
  }

  if (isEventEnded(event)) {
    return 'Zakończone';
  }

  return isEventActive(event) ? 'Aktywne' : 'Nieaktywne';
};

export const getEventStatusClassName = (event, matchingPendingItem = null) => {
  if (event?.hasPendingEdit === true) {
    const pendingItem = matchingPendingItem || event?.pendingItem || event?.pendingChange || null;
    if (isPendingDeactivation(pendingItem, event)) {
      return 'event-status event-status--deactivation-request';
    }
    if (isPendingActivation(pendingItem, event)) {
      return 'event-status event-status--activation-request';
    }
    return 'event-status event-status--data-change';
  }

  if (isApprovedByAdmin(event) === false) {
    return 'event-status event-status--pending';
  }

  if (isEventEnded(event)) {
    return 'event-status event-status--ended';
  }

  if (!isEventActive(event)) {
    return 'event-status event-status--inactive';
  }

  return 'event-status event-status--active';
};

const promptRejectReason = (initialReason = '') => {
  const value = window.prompt('Podaj powod odrzucenia:', initialReason);
  if (value === null) {
    return null;
  }

  const reason = value.trim();
  if (!reason) {
    alert('Powod odrzucenia jest wymagany.');
    return null;
  }

  return reason;
};

export const openEventDetailsModal = (event, dispatch) => {
  dispatch(setAddEventMode(false));
  dispatch(clearPendingEditReview());
  dispatch(setModalViewActualTab('Dane podstawowe'));
  dispatch(setEditedEvent(event.id));
  dispatch(openEditModal());
};

export const openEventPendingEditReview = async (event, dispatch, preloadedPendingItem = null) => {
  try {
    let pendingItem = preloadedPendingItem;
    if (!pendingItem) {
      const pendingEdits = await ApiService.getPendingEventEdits();
      pendingItem = (Array.isArray(pendingEdits) ? pendingEdits : []).find(
        (item) => String(item?.eventId ?? item?.id) === String(event.id)
      );
    }

    let payload = {};
    const rawPayload = pendingItem?.pendingChange?.payloadJson ?? pendingItem?.payloadJson ?? event?.pendingChange?.payloadJson;
    if (rawPayload && typeof rawPayload === 'object') {
      payload = rawPayload;
    } else {
      try {
        payload = JSON.parse(rawPayload || '{}');
        if (typeof payload === 'string') {
          payload = JSON.parse(payload || '{}');
        }
      } catch (parseError) {
        payload = {};
      }
    }

    const isDeactivation = isPendingDeactivation(pendingItem || payload, event);
    const isActivation = isPendingActivation(pendingItem || payload, event);

    dispatch(setAddEventMode(false));
    dispatch(setModalViewActualTab('Dane podstawowe'));
    dispatch(setEditedEvent(event.id));
    dispatch(clearPendingEditReview());
    dispatch(setPendingEditReview({
      eventId: event.id,
      pendingId: pendingItem?.id ?? pendingItem?.eventId ?? event.id,
      payload,
      isDeactivation,
      isActivation,
      requestedAt: pendingItem?.pendingChange?.requestedAt ?? pendingItem?.requestedAt ?? event?.pendingEditRequestedAt ?? null,
    }));
    dispatch(openEditModal());
  } catch (error) {
    console.error('Blad otwarcia podgladu zmian wydarzenia:', error);
  }
};

export const getEventActionItems = (event, dispatch, onDeleteEvent, matchingPendingItem = null) => {
  const isPendingEdit = event?.hasPendingEdit === true;
  const isPendingNewEvent = isApprovedByAdmin(event) === false;
  const pendingItem = matchingPendingItem || event?.pendingItem || event?.pendingChange || null;
  const isDeactivation = isPendingEdit && isPendingDeactivation(pendingItem, event);
  const isActivation = isPendingEdit && isPendingActivation(pendingItem, event);

  const actionItems = [];

  if (isPendingEdit) {
    const approveLabel = isDeactivation
      ? 'Podgląd i akceptacja dezaktywacji'
      : isActivation
      ? 'Podgląd i akceptacja aktywacji'
      : 'Podgląd i akceptacja zmian';
    const rejectLabel = isDeactivation || isActivation ? 'Odrzuć wniosek' : 'Odrzuć zmianę';

    actionItems.push(
      {
        key: 'approve-pending-edit',
        label: approveLabel,
        icon: TickCircle,
        onClick: () => openEventPendingEditReview(event, dispatch, matchingPendingItem),
      },
      {
        key: 'reject-pending-edit',
        label: rejectLabel,
        icon: Danger,
        danger: true,
        onClick: async () => {
          const reason = promptRejectReason();
          if (!reason) {
            return;
          }

          try {
            await ApiService.rejectPendingEventEdit({ id: event.id, reason });
            dispatch(toggleNeedRefresh());
          } catch (error) {
            console.error('Blad odrzucenia zmiany wydarzenia:', error);
          }
        },
      },
      {
        key: 'edit',
        label: 'Edytuj',
        icon: Edit,
        onClick: () => openEventDetailsModal(event, dispatch),
      },
      {
        key: 'stats',
        label: 'Statystyki',
        icon: StatusUp,
        onClick: () => {
          dispatch(setSelectedEvent(event.id));
          window.location.href = `/statistics?view=event&eventId=${event.id}`;
        },
      },
      {
        key: 'duplicate',
        label: 'Duplikuj',
        icon: TaskSquare,
        onClick: () => {
          dispatch(setAddEventMode(true));
          dispatch(clearPendingEditReview());
          dispatch(setModalViewActualTab('Dane podstawowe'));
          dispatch(setEditedEvent(null));
          dispatch(setDuplicatedEventId(event.id));
          dispatch(toggleAddModal());
        },
      }
    );
  } else if (isPendingNewEvent) {
    actionItems.push(
      {
        key: 'approve',
        label: 'Podgląd i akceptacja',
        icon: TickCircle,
        onClick: () => openEventDetailsModal(event, dispatch),
      },
      {
        key: 'reject',
        label: 'Odrzuć',
        icon: Danger,
        danger: true,
        onClick: () => openEventDetailsModal(event, dispatch),
      },
      {
        key: 'edit',
        label: 'Edytuj',
        icon: Edit,
        onClick: () => openEventDetailsModal(event, dispatch),
      },
      {
        key: 'stats',
        label: 'Statystyki',
        icon: StatusUp,
        onClick: () => {
          dispatch(setSelectedEvent(event.id));
          window.location.href = `/statistics?view=event&eventId=${event.id}`;
        },
      },
      {
        key: 'duplicate',
        label: 'Duplikuj',
        icon: TaskSquare,
        onClick: () => {
          dispatch(setAddEventMode(true));
          dispatch(clearPendingEditReview());
          dispatch(setModalViewActualTab('Dane podstawowe'));
          dispatch(setEditedEvent(null));
          dispatch(setDuplicatedEventId(event.id));
          dispatch(toggleAddModal());
        },
      },
      {
        key: 'deactivate',
        label: isEventMarkedActive(event) ? 'Dezaktywuj' : 'Aktywuj',
        icon: isEventMarkedActive(event) ? Pause : Play,
        disabled: isEventEnded(event),
        onClick: async () => {
          if (isEventEnded(event)) return;
          try {
            await ApiService.updateEvent(event.id, { isActive: !isEventMarkedActive(event) });
            dispatch(toggleNeedRefresh());
          } catch (error) {
            console.error('Blad zmiany statusu wydarzenia:', error);
          }
        },
      }
    );
  } else {
    actionItems.push(
      {
        key: 'details',
        label: 'Szczegóły',
        icon: StatusUp,
        onClick: () => openEventDetailsModal(event, dispatch),
      },
      {
        key: 'edit',
        label: 'Edytuj',
        icon: Edit,
        onClick: () => openEventDetailsModal(event, dispatch),
      },
      {
        key: 'stats',
        label: 'Statystyki',
        icon: StatusUp,
        onClick: () => {
          dispatch(setSelectedEvent(event.id));
          window.location.href = `/statistics?view=event&eventId=${event.id}`;
        },
      },
      {
        key: 'duplicate',
        label: 'Duplikuj',
        icon: TaskSquare,
        onClick: () => {
          dispatch(setAddEventMode(true));
          dispatch(clearPendingEditReview());
          dispatch(setModalViewActualTab('Dane podstawowe'));
          dispatch(setEditedEvent(null));
          dispatch(setDuplicatedEventId(event.id));
          dispatch(toggleAddModal());
        },
      },
      {
        key: 'deactivate',
        label: isEventMarkedActive(event) ? 'Dezaktywuj' : 'Aktywuj',
        icon: isEventMarkedActive(event) ? Pause : Play,
        disabled: isEventEnded(event),
        onClick: async () => {
          if (isEventEnded(event)) return;
          try {
            await ApiService.updateEvent(event.id, { isActive: !isEventMarkedActive(event) });
            dispatch(toggleNeedRefresh());
          } catch (error) {
            console.error('Blad zmiany statusu wydarzenia:', error);
          }
        },
      }
    );
  }

  actionItems.push({
    key: 'delete',
    label: 'Usuń',
    icon: Trash,
    danger: true,
    onClick: () => onDeleteEvent(event.id),
  });

  return actionItems;
};

export const fetchUsers = async (
  sortBy,
  sortDirection,
  currentPage,
  dispatch,
  setMaxPages,
  setRows,
  checkedItems,
  keyword,
  onDeleteEvent,
  filterQuery = 'ALL',
  pageSize = 25
) => {
  try {
    const checkedSortBy = sortBy === null || sortDirection === null ? null : sortBy;
    const checkedSortDirection = sortBy === null || sortDirection === null ? null : sortDirection;
    const [responseCategories, response, pendingEditsResult] = await Promise.all([
      ApiService.getEventsCategories().catch(() => ({ data: [] })),
      ApiService.getEvents(checkedSortBy, checkedSortDirection, currentPage, keyword, filterQuery, pageSize),
      ApiService.getPendingEventEdits().catch(() => []),
    ]);

    const categories = responseCategories?.data || [];
    const eventsData = Array.isArray(response?.data) ? response.data : [];
    const pendingEditsList = Array.isArray(pendingEditsResult)
      ? pendingEditsResult
      : Array.isArray(pendingEditsResult?.data)
      ? pendingEditsResult.data
      : [];
    const pendingEditsMap = new Map(
      pendingEditsList.map((entry) => [String(entry?.eventId ?? entry?.id), entry])
    );

    dispatch(setMaxPages(response?.maxPage ?? 1));
    const totalCount = response?.totalElements ?? response?.totalCount ?? eventsData.length;
    dispatch(setTotalCount(totalCount));

    const mappedRows = eventsData.map((event) => {
      const matchingPendingItem = pendingEditsMap.get(String(event.id)) || null;

      return [
        <label
          key={`checkbox-${event.id}`}
          className="checkbox-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={checkedItems && checkedItems.includes(event.id)}
            onChange={() => dispatch(toggleCheckedItem(event.id))}
          />
        </label>,
        event.id,
        <div>
          <ActionMenu
            items={getEventActionItems(event, dispatch, onDeleteEvent, matchingPendingItem)}
          />
        </div>,
        formatDate(matchingPendingItem?.pendingChange?.requestedAt ?? matchingPendingItem?.requestedAt ?? event.pendingEditRequestedAt ?? event.pendingRequestedAt ?? event.added_on_utc),
        <span
          key={`event-name-${event.id}`}
          className="company-name-link"
          onClick={() => {
            if (event?.hasPendingEdit === true) {
              openEventPendingEditReview(event, dispatch, matchingPendingItem);
            } else {
              openEventDetailsModal(event, dispatch);
            }
          }}
          title="Kliknij, aby otworzyć szczegóły wydarzenia"
        >
          {event.name}
        </span>,
        <div>
          {event.city} - {event.place}
        </div>,
        formatDate(event.startDate),
        formatDate(event.endDate),
        categories.find((cat) => cat.id === event.categoryId)?.name || 'Brak kategorii',
        <span className={getEventStatusClassName(event, matchingPendingItem)}>
          {getEventStatusLabel(event, matchingPendingItem)}
        </span>,
      ];
    });

    setRows(mappedRows);
  } catch (error) {
    console.error('Blad podczas pobierania danych:', error);
  }
};
